import { useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { useAudioStore } from '../../stores/audioStore';
import { useListenStore } from '../../stores/listenStore';
import { useAuthStore } from '../../stores/authStore';
import type { SongSummary, SubsetWithSongs } from '../../types';

interface SongCardProps {
  song: SongSummary;
  onClickCard: (song: SongSummary) => void;
  voteRank?: number; // Optional: rank if user voted for this song
  voteIsSubmitted?: boolean; // Optional: whether the vote is submitted (vs draft)
  specialCategoryIndices?: number[]; // Optional: indices of special categories this song is winning
  subset?: SubsetWithSongs | null; // Optional: subset data for category names
}

export function SongCard({ song, onClickCard, voteRank, voteIsSubmitted = true, specialCategoryIndices, subset }: SongCardProps) {
  const { currentSong, isPlaying, playSong, pause, resume } = useAudioStore();
  const { hasListened } = useListenStore();
  const { user } = useAuthStore();
  const [imageLoaded, setImageLoaded] = useState(false);
  const isCurrentSong = currentSong?.id === song.id;

  // Check if listen history feature is enabled and song is unlistened
  const listenHistoryEnabled = user?.preferences?.listenHistoryEnabled !== false;
  const isUnlistened = listenHistoryEnabled && !hasListened(song);
  const borderColor = user?.preferences?.newSongBorderColor || '#7ba3d6';

  // Check if vote ranks should be shown
  const showVoteRanks = user?.preferences?.showVoteRanks !== false;

  // Helper to get emoji icon for a special category
  const getCategoryIcon = (categoryName: string): string => {
    const name = categoryName.toLowerCase();
    if (name.includes('story')) return '🎭';
    if (name.includes('heart')) return '💝';
    if (name.includes('fun')) return '🎉';
    if (name.includes('energy')) return '⚡';
    if (name.includes('cover')) return '🎨';
    return '⭐';
  };

  const handlePlayButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isCurrentSong) {
      if (isPlaying) {
        pause();
      } else {
        resume();
      }
    } else {
      playSong(song, true); // All plays are manual/pinned
    }
  };

  const handleCardClick = () => {
    onClickCard(song);
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className="group relative aspect-square cursor-pointer rounded-lg overflow-hidden bg-monster-card border-2 hover:border-monster-blue transition-all"
      style={{
        borderColor: isUnlistened ? borderColor : 'transparent',
        boxShadow: isUnlistened ? `0 0 8px ${borderColor}40` : 'none',
      }}
      onClick={handleCardClick}
    >
      {/* Album Art */}
      <img
        src={song.imagePath}
        alt={song.title}
        loading="lazy"
        onLoad={() => setImageLoaded(true)}
        className={`w-full h-full object-cover transition-all duration-300 ${
          imageLoaded ? 'opacity-100 blur-0' : 'opacity-0 blur-sm'
        }`}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

      {/* Song Info */}
      <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
        <h3 className="font-semibold text-sm line-clamp-1">{song.title}</h3>
        <p className="text-xs text-gray-300 line-clamp-1">{song.artist}</p>
        {song.durationSeconds && (
          <p className="text-xs text-gray-400 mt-1">{formatDuration(song.durationSeconds)}</p>
        )}
      </div>

      {/* Play Button Overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handlePlayButtonClick}
          className="w-16 h-16 flex items-center justify-center rounded-full bg-monster-blue/90 hover:bg-monster-blue hover:scale-110 transition-all shadow-lg"
        >
          {isCurrentSong && isPlaying ? (
            <Pause size={28} fill="white" />
          ) : (
            <Play size={28} fill="white" className="ml-1" />
          )}
        </button>
      </div>

      {/* Now Playing Indicator */}
      {isCurrentSong && isPlaying && (
        <div className="absolute top-2 right-2">
          <div className="flex items-center gap-1">
            <div className="w-1 h-3 bg-monster-blue animate-pulse" />
            <div className="w-1 h-4 bg-monster-blue animate-pulse" style={{ animationDelay: '0.2s' }} />
            <div className="w-1 h-3 bg-monster-blue animate-pulse" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>
      )}

      {/* Vote Rank Badge */}
      {showVoteRanks && voteRank && (
        <div className="absolute top-2 left-2">
          <div className={`font-bold text-sm w-7 h-7 rounded-full flex items-center justify-center shadow-lg border-2 ${
            voteIsSubmitted
              ? 'bg-monster-crimson text-white border-white/30'
              : 'bg-blue-600/70 text-white border-blue-400/50'
          }`}
          title={voteIsSubmitted ? `Rank #${voteRank} (submitted)` : `Rank #${voteRank} (draft - not yet submitted)`}
          >
            {voteRank}
          </div>
        </div>
      )}

      {/* Special Category Badges */}
      {specialCategoryIndices && specialCategoryIndices.length > 0 && subset && (
        <div className="absolute bottom-2 right-2 flex gap-1">
          {specialCategoryIndices.map((categoryIndex) => {
            const category = subset.specialCategories[categoryIndex];
            if (!category || !category.enabled) return null;

            const icon = getCategoryIcon(category.name);

            return (
              <div
                key={categoryIndex}
                title={category.name}
                className="text-lg leading-none bg-black/60 backdrop-blur-sm rounded px-1.5 py-0.5 shadow-lg border border-white/20"
              >
                {icon}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
